import {
  CalendarEvent,
  CalendarEventTypeConfirmed,
  CalendarEventTypeOther,
  CalendarEventTypePending,
  CalendarEventTypesMapping,
} from './calendar.model'
import * as R from 'ramda'
import * as lodash from 'lodash'
import * as moment from 'moment'
import {
  Account,
  createdBySystemNameMappings,
  epochToView,
  epochToViewUTC,
  Rental,
  RentalService,
} from '@tokeet-frontend/tv3-platform'
import { DefaultInquiryColor } from '@tv3/store/inquiry/inquiry.model'
import { AgendaEventType } from '@tv3/containers/calendar/agenda/enums/agenda-calendar-event-type.enum'
import { parseInquirySource } from '../inquiry/inquiry-source-view'

export const DefaultHoldEventColor = '#676767'
const AirbnbBookingRequestColor = '#dc2127'

export class CalendarEventView extends CalendarEvent {
  // data fields
  startDate: moment.Moment
  endDate: moment.Moment
  isAllDay: boolean
  isHoldEvent: boolean
  color: string

  // view fields
  titleView = ''
  guestsView = ''
  rentalView = ''
  checkinView = ''
  checkoutView = ''
  typeView = ''
  sourceView = ''

  constructor(rawEvent: CalendarEvent, public rental: Rental, account: Account) {
    super(rawEvent)

    this.sourceView = parseInquirySource(this.source)?.view || this.source

    this.startDate = R.is(Number, rawEvent.start) ? moment.utc(rawEvent.start * 1000) : moment.utc(rawEvent.start)
    this.endDate = R.is(Number, rawEvent.end) ? moment.utc(rawEvent.end * 1000) : moment.utc(rawEvent.end)

    this.isHoldEvent = !rawEvent.bookingId
    this.isAllDay = this.isHoldEvent
    if (R.isNil(this.type)) {
      this.type = rawEvent.bookingId ? CalendarEventTypeConfirmed.id : CalendarEventTypeOther.id
    }
    this.color = this.isHoldEvent ? rawEvent.color || DefaultHoldEventColor : rawEvent.color || DefaultInquiryColor

    this.titleView = this.title
    this.typeView = CalendarEventTypesMapping[this.type].name
    if (!this.isHoldEvent && this.source === 'airbnb' && this.status === 'pending') {
      this.type = CalendarEventTypePending.id
      this.typeView = $localize`Request to Book`
      this.color = AirbnbBookingRequestColor
    }
    if (rental) {
      this.checkinView = RentalService.getCheckInTime(rental, this.checkin, account).format('h:mm A')
      this.checkoutView = RentalService.getCheckOutTime(rental, this.checkout, account).format('h:mm A')
      this.rentalView = rental?.nickname || rental?.name
    }

    this.guestsView = rawEvent.bookingId
      ? $localize`Adults` + ` ${rawEvent.adults}, ` + $localize`Children` + ` ${rawEvent.children}`
      : $localize`Hold event`
  }
}

export class AgendaCalendarEventView extends CalendarEventView {
  // data fields
  checkEventType: string
  date: moment.Moment

  // view fields
  dateView = ''
  checkTimeView = ''

  constructor(event: CalendarEvent, rental: Rental, account: Account) {
    super(event, rental, account)
  }

  setCheckEventType(type: AgendaEventType) {
    this.checkEventType = type
    this.date = type === AgendaEventType.CheckOut ? this.endDate : this.startDate
    this.dateView = this.date.format('DD - MMM - YYYY')
    this.checkTimeView = type === AgendaEventType.CheckOut ? this.checkoutView : this.checkinView
  }
}

export class StandardCalendarEventView extends CalendarEventView {
  constructor(event: CalendarEvent, rental: Rental, account: Account) {
    super(event, rental, account)

    const sourceView = parseInquirySource(this.source)

    const source = !this.isHoldEvent && this.source ? `(${lodash.startCase(sourceView.view)}) ` : ''
    this.titleView = `${event.title} ${source}(${this.typeView}) ${!this.rentalView ? '' : ' - ' + this.rentalView}`
  }
}

export class PlanningCalendarEventView extends CalendarEventView {
  constructor(event: CalendarEvent, rental: Rental, account: Account) {
    super(event, rental, account)
    this.titleView = `${event.title}`
  }
}

export class MultiCalendarEventView extends CalendarEventView {
  constructor(event: CalendarEvent, rental: Rental, account: Account) {
    super(event, rental, account)

    const sourceView = parseInquirySource(this.source)

    const source = !this.isHoldEvent && this.source ? `(${lodash.startCase(sourceView.view)}) ` : ''
    this.titleView = `${event.title} ${source}(${this.typeView}) ${!this.rentalView ? '' : ' - ' + this.rentalView}`
  }
}

export class HoldEventView extends CalendarEventView {
  createdView: string
  startView: string
  endView: string
  expiresView: string
  // TV4-5596: tooltips/exports show date + rental check-in/out time. The
  // epoch's own time is an FE-supplied default and is not meaningful.
  startTooltipView: string
  endTooltipView: string
  startCsvView: string
  endCsvView: string
  dateViewFormat = 'DD - MMM - YYYY'
  createdByView: string
  isAuto: boolean

  constructor(event: CalendarEvent, rental: Rental, account: Account) {
    super(event, rental, account)
    this.isAuto = !!createdBySystemNameMappings[event.createdBy]
    this.createdView = epochToView(this.created, this.dateViewFormat)
    this.startView = epochToViewUTC(this.start)
    this.endView = epochToViewUTC(this.end)
    this.expiresView = epochToViewUTC(this.expires)

    const checkin = rental ? RentalService.getCheckInTime(rental, undefined, account) : null
    const checkout = rental ? RentalService.getCheckOutTime(rental, undefined, account) : null
    this.startTooltipView = HoldEventView.formatBoundary(
      this.start,
      checkin,
      'DD - MMM - YYYY h:mm A',
      'DD - MMM - YYYY'
    )
    this.endTooltipView = HoldEventView.formatBoundary(this.end, checkout, 'DD - MMM - YYYY h:mm A', 'DD - MMM - YYYY')
    this.startCsvView = HoldEventView.formatBoundary(this.start, checkin, 'YYYY-MM-DD HH:mm', 'YYYY-MM-DD')
    this.endCsvView = HoldEventView.formatBoundary(this.end, checkout, 'YYYY-MM-DD HH:mm', 'YYYY-MM-DD')
  }

  private static formatBoundary(
    epoch: number,
    rentalTime: moment.Moment | null,
    withTimeFormat: string,
    dateOnlyFormat: string
  ): string {
    if (!epoch) return ''
    const dt = moment.utc(epoch * 1000)
    if (!rentalTime) return dt.format(dateOnlyFormat)
    dt.set({ hour: rentalTime.hour(), minute: rentalTime.minute(), second: 0 })
    return dt.format(withTimeFormat)
  }
}
